import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '@/shared/lib/i18n';
import { TriangleAlertIcon } from '@/shared/ui/icons';
import { Button } from '@/shared/ui/buttons/Button';

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  error: Error | null;
};

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[AppErrorBoundary] Uncaught error', error, info);
  }

  private reset = () => {
    this.setState({ error: null });
  };

  private goHome = () => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.error) {
      const t = i18n.getFixedT(null, 'common');
      return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-50 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-lg">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
              <TriangleAlertIcon className="h-8 w-8 text-red-500" aria-hidden />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-slate-900">
              {t('errorBoundary.title', 'Kutilmagan xatolik')}
            </h1>
            <p className="mb-6 text-sm text-slate-600">
              {t(
                'errorBoundary.description',
                'Iltimos qaytadan urinib koʻring. Muammo davom etsa bosh sahifaga qayting.',
              )}
            </p>
            <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
              <Button variant="primary" onClick={this.reset}>
                {t('errorBoundary.retry', 'Qayta urinish')}
              </Button>
              <Button variant="outline" onClick={this.goHome}>
                {t('errorBoundary.goHome', 'Bosh sahifaga qaytish')}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
